import { useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { FaSignOutAlt } from 'react-icons/fa';
import { APP_ENV, APP_VERSION } from '../config';
import { usePermissions } from '../context/PermissionsContext';
import { useTenant } from '../context/TenantContext';
import { formatAppVersionLabel } from '../utils/appRelease';

function displayUserName(name, email) {
    const trimmed = typeof name === 'string' ? name.trim() : '';
    return trimmed || email || 'User';
}

function avatarInitial(label) {
    const ch = (label || '?').trim().charAt(0);
    return ch ? ch.toUpperCase() : '?';
}

function formatRoles(roles) {
    if (!Array.isArray(roles) || roles.length === 0) return '';
    return roles.join(', ');
}

function SidebarUserProfile({ collapsed, onLogout }) {
    const { name, email, roles, tenantData } = usePermissions();
    const { currentTenant } = useTenant();
    const [showModal, setShowModal] = useState(false);

    const displayName = displayUserName(name, email);
    const initial = avatarInitial(displayName);
    const roleLabel = formatRoles(roles);
    const tenantName = currentTenant?.name || '';
    const subtitle = [roleLabel, tenantName].filter(Boolean).join(' · ');
    const versionLabel = formatAppVersionLabel(APP_VERSION, APP_ENV);
    const tooltipParts = [displayName, roleLabel, tenantName].filter(Boolean);
    const tooltip = tooltipParts.join(' · ');

    const tenantRows = useMemo(() => {
        const currentId = currentTenant?.id != null ? String(currentTenant.id) : '';
        const rows = (tenantData || []).map((entry) => ({
            id: entry.tenant_id,
            name: entry.tenant_name || 'Unknown tenant',
            roles: formatRoles(entry.roles),
            isCurrent: currentId !== '' && String(entry.tenant_id) === currentId,
        }));
        rows.sort((a, b) => {
            if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;
            return String(a.name).localeCompare(String(b.name));
        });
        return rows;
    }, [tenantData, currentTenant?.id]);

    return (
        <>
            <div className={`sidebar-account ${collapsed ? 'sidebar-account--collapsed' : ''}`}>
                <button
                    type="button"
                    className={`sidebar-user-profile ${collapsed ? 'sidebar-user-profile--collapsed' : ''}`}
                    onClick={() => setShowModal(true)}
                    title={tooltip}
                    aria-label={`Open profile for ${displayName}`}
                >
                    <span className="sidebar-user-avatar" aria-hidden="true">{initial}</span>
                    <span className={`sidebar-user-meta ${collapsed ? 'sidebar-user-meta--hidden' : ''}`}>
                        <span className="sidebar-user-name">{displayName}</span>
                        {subtitle ? (
                            <span className="sidebar-user-sub">
                                {roleLabel ? <span className="sidebar-user-role">{roleLabel}</span> : null}
                                {roleLabel && tenantName ? <span aria-hidden="true"> · </span> : null}
                                {tenantName ? <span>{tenantName}</span> : null}
                            </span>
                        ) : null}
                    </span>
                </button>
                <button
                    type="button"
                    className={`sidebar-signout ${collapsed ? 'sidebar-signout--collapsed' : ''}`}
                    onClick={onLogout}
                    title={collapsed ? 'Sign out' : undefined}
                >
                    <FaSignOutAlt className="sidebar-signout-icon" />
                    {!collapsed && <span>Sign out</span>}
                </button>
                {!collapsed && versionLabel ? (
                    <div className="sidebar-app-version" title={versionLabel}>{versionLabel}</div>
                ) : null}
            </div>

            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                centered
                className="user-profile-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Profile</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="user-profile-modal__identity">
                        <span className="user-profile-modal__avatar" aria-hidden="true">{initial}</span>
                        <div>
                            <div className="user-profile-modal__name">{displayName}</div>
                            {email ? <div className="user-profile-modal__email">{email}</div> : null}
                            {versionLabel ? <div className="user-profile-modal__version">{versionLabel}</div> : null}
                        </div>
                    </div>

                    <h3 className="user-profile-modal__heading">Tenants</h3>
                    {tenantRows.length === 0 ? (
                        <p className="user-profile-modal__empty">No tenants available.</p>
                    ) : (
                        <ul className="user-profile-modal__tenants">
                            {tenantRows.map((row) => (
                                <li
                                    key={row.id || row.name}
                                    className={`user-profile-modal__tenant${row.isCurrent ? ' is-current' : ''}`}
                                >
                                    <div className="user-profile-modal__tenant-name">
                                        {row.name}
                                        {row.isCurrent ? (
                                            <span className="user-profile-modal__current-badge">Current</span>
                                        ) : null}
                                    </div>
                                    <div className="user-profile-modal__tenant-role">
                                        {row.roles || 'No role'}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Close
                    </Button>
                    <Button
                        variant="outline-danger"
                        onClick={() => {
                            setShowModal(false);
                            onLogout();
                        }}
                    >
                        <FaSignOutAlt /> Sign out
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default SidebarUserProfile;
